import styled from '@emotion/styled';
import { forwardRef } from 'react';

import { theme } from '../../theme';
import type { MetadataDetailsProps, FontSizeVariant, MetadataLayout } from './MetadataDetails.types';

/**
 * Map font size variants to actual pixel values from theme
 */
const fontSizeMap: Record<FontSizeVariant, string> = {
  xs: theme.typography.fontSize.xs,
  sm: theme.typography.fontSize.sm,
  base: theme.typography.fontSize.base,
  md: theme.typography.fontSize.md,
  lg: theme.typography.fontSize.lg,
  xl: theme.typography.fontSize.xl,
};

interface MetadataContainerProps {
  layout: MetadataLayout;
}

const MetadataContainer = styled.div<MetadataContainerProps>`
  display: flex;
  padding-bottom: 16px;
  flex-wrap: wrap;
  flex-direction: ${props => props.layout === 'stacked' ? 'column' : 'row'};
`;

interface NameFieldProps {
  fieldColor: string;
  fieldMinWidth: string | number;
  fieldFontSize?: string;
  layout: MetadataLayout;
}

const NameField = styled.div<NameFieldProps>`
  color: ${props => props.fieldColor};
  min-width: ${props =>
    props.layout === 'stacked' 
      ? 'auto'
      : typeof props.fieldMinWidth === 'number'
        ? `${props.fieldMinWidth}px`
        : props.fieldMinWidth};
  font-style: normal;
  font-weight: 400;
  font-size: ${props => props.fieldFontSize || '11px'};
  line-height: 20px;
  text-transform: uppercase;
  margin-right: ${props => props.layout === 'stacked' ? '0' : '10px'};
  margin-bottom: ${props => props.layout === 'stacked' ? '4px' : '0'};
`;

const ChipDataContainer = styled.div`
  display: inline-flex;
  background: var(--ib-color-grey-100, #f7f7f7);
  border-radius: 7px;
  padding: 6px 15px;
  flex-direction: column;
  margin-right: 8px;
  margin-bottom: 16px;
`;

interface ChipNameFieldProps {
  fieldFontSize?: string;
}

const ChipNameField = styled.div<ChipNameFieldProps>`
  font-style: normal;
  font-weight: 400;
  font-size: ${props => props.fieldFontSize || '11px'};
  line-height: 20px;
  text-transform: uppercase;
  color: var(--ib-color-grey-500, #737373);
`;

interface ChipValueFieldProps {
  fieldFontSize?: string;
}

const ChipValueField = styled.div<ChipValueFieldProps>`
  font-style: normal;
  font-weight: 400;
  font-size: ${props => props.fieldFontSize || '14px'};
  line-height: 20px;
  color: var(--ib-color-grey-900, #191919);
`;

interface BodyWrapperProps {
  fieldFontSize?: string;
}

const BodyWrapper = styled.span<BodyWrapperProps>`
  max-width: 100%;
  word-break: break-word;
  white-space: pre-wrap;
  font-style: normal;
  font-weight: 400;
  font-size: ${props => props.fieldFontSize || '14px'};
  line-height: 20px;
  color: var(--ib-color-text-primary, #4a4a4a);
`;

interface SemiLabelWrapperProps {
  fieldFontSize?: string;
}

const SemiLabelWrapper = styled.span<SemiLabelWrapperProps>`
  max-width: 100%;
  word-break: break-word;
  font-style: normal;
  font-weight: 500;
  font-size: ${props => props.fieldFontSize || '14px'};
  line-height: 20px;
  color: var(--ib-color-text-primary, #4a4a4a);
`;

const CustomContainer = styled.div`
  max-width: 100%;
`;

/**
 * MetadataDetails displays a labeled data field in various formats.
 *
 * Supports four display types:
 * - Normal: Standard label-value pair
 * - Bold: Label with emphasized (semi-bold) value
 * - Chip: Compact chip-style display with background
 * - Custom: Label with custom component as value
 *
 * Layout options:
 * - inline: Label on the left, value on the right (default)
 * - stacked: Label above, value below
 *
 * Font sizes can be customized using theme-aligned variants:
 * - labelFontSize: Controls the label text size
 * - valueFontSize: Controls the value text size
 *
 * @example
 * // Normal usage
 * <MetadataDetails name="Name" value="John Doe" />
 *
 * @example
 * // Stacked layout with custom font sizes
 * <MetadataDetails
 *   name="Status"
 *   value="Active"
 *   layout="stacked"
 *   valueFontSize="xs"
 * />
 *
 * @example
 * // Bold type
 * <MetadataDetails name="Title" value="Project Manager" type="Bold" />
 *
 * @example
 * // Chip type (always stacked internally)
 * <MetadataDetails name="Status" value="Active" type="Chip" />
 *
 * @example
 * // Custom component
 * <MetadataDetails
 *   name="Link"
 *   type="Custom"
 *   component={<a href="#">View Details</a>}
 * />
 */
export const MetadataDetails = forwardRef<HTMLDivElement, MetadataDetailsProps>(
  (
    {
      name = '',
      value = '',
      type = 'Normal',
      layout = 'inline',
      component,
      color,
      minWidth = '140px',
      labelFontSize,
      valueFontSize,
      className,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const defaultColor = 'var(--ib-color-grey-500, #737373)';
    const fieldColor = color || defaultColor;
    const labelSize = labelFontSize ? fontSizeMap[labelFontSize] : undefined;
    const valueSize = valueFontSize ? fontSizeMap[valueFontSize] : undefined;

    if (type === 'Chip') {
      return (
        <ChipDataContainer
          ref={ref}
          className={className}
          data-testid={dataTestId}
          data-id={dataId}
        >
          <ChipNameField fieldFontSize={labelSize}>{name}</ChipNameField>
          <ChipValueField fieldFontSize={valueSize}>{value}</ChipValueField>
        </ChipDataContainer>
      );
    }

    const ValueField = type === 'Bold' ? SemiLabelWrapper : BodyWrapper;

    return (
      <MetadataContainer
        ref={ref}
        className={className}
        layout={layout}
        data-testid={dataTestId}
        data-id={dataId}
      >
        <NameField 
          fieldColor={fieldColor} 
          fieldMinWidth={minWidth} 
          fieldFontSize={labelSize}
          layout={layout}
        >
          {name}
        </NameField>

        {type === 'Custom' ? (
          <CustomContainer>{component}</CustomContainer>
        ) : (
          <ValueField fieldFontSize={valueSize}>{value}</ValueField>
        )}
      </MetadataContainer>
    );
  }
);

MetadataDetails.displayName = 'MetadataDetails';
